import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { CaretLeft, CaretRight, Info } from '@phosphor-icons/react'
import { Icon, IconButton, Tabs, TabsList, TabsTrigger } from '@shopflo/ui'
import { activateAppV2, deactivateAppV2, updateAppTierV2 } from 'src/lib/core/api-methods'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import { handleAppInstallAction } from 'src/lib/utils/AppStoreHelper'
import { APP_STORE_CATEGORY_MAP, humanizeCategoryKey } from 'src/assets/appsIntegrationsConfig'
import {
  appsIntegrationsListPath,
  getActionErrorMessage,
  getCurrentPlanLogoUrl,
  getCurrentPlanName,
  isMeaningfullyConfigured,
  parseAppTierFeatureConfigs,
  sumFeaturePriceFromTiers
} from 'src/lib/utils/appsIntegrationsHelpers'
import { permissionType, TOO_MANY_ACTIVE_UPSELLS_ERROR_CODE } from 'src/lib/utils/constants'
import {
  type AppIntegrationDetailViewProps,
  type AppMenuAction,
  type AppTierFeaturePlanConfig,
  type DetailTab,
  type TopLevelTab,
  type V2AppTierUpdatePayload,
  UpsellActivityAction
} from 'src/lib/types/appsIntegrations'
import AppDetailSidebar from './AppDetailSidebar'
import AppLogo from './AppLogo'
import CancellationReasonDialog, { CancellationReasonFormData } from './dialog/CancellationReasonDialog'
import DeleteConfirmationPopup from 'src/components/common/dialog/DeleteConfirmationPopup'
import ConfigSchemaForm, { ConfigSchemaFormHandle } from './ConfigSchemaForm'
import IntegrationDetailSidebar from './IntegrationDetailSidebar'
import DetailMediaCarousel from './DetailMediaCarousel'
import { useLocale } from 'src/lib/hooks/useLocal'
import { useUser } from 'src/lib/contexts/UserContext'
import { useActiveAppExtension, findAppExtension } from 'src/lib/hooks/appExtensions/useActiveAppExtension'
import { EntityName, useActivityLogs } from 'src/lib/hooks/useActivityLogs'
import { useUpsellPlan } from 'src/lib/hooks/useUpsellPlan'
import type { AppExtensionActivateResult } from 'src/lib/hooks/appExtensions/types'
import { classNames, getErrorCode } from 'src/lib/utils/helpers'

const splitHowItWorks = (raw?: string): string[] => {
  if (!raw) return []
  const trimmed = raw.trim()
  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed)
      if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean)
    } catch {}
  }
  return trimmed
    .split('\n')
    .map(l => l.trim())
    .filter(Boolean)
}

const syncDetailTabToUrl = (tab: DetailTab) => {
  if (typeof window === 'undefined') return
  const params = new URLSearchParams(window.location.search)
  if (tab === 'about') params.delete('tab')
  else params.set('tab', tab)
  const qs = params.toString()
  const target = `${window.location.pathname}${qs ? `?${qs}` : ''}`
  const prev = window.history.state || {}
  window.history.replaceState({ ...prev, url: target, as: target }, '', target)
}

const HIGHLIGHT_OVERLAY_CLASS = 'absolute inset-0 pointer-events-none rounded-xl z-20 bg-yellow-300/20 opacity-0'

const getDetailTabList = (source: 'app' | 'integration'): { key: DetailTab; label: string }[] =>
  source === 'integration'
    ? [
        { key: 'about', label: 'About' },
        { key: 'configuration', label: 'Configuration' }
      ]
    : [
        { key: 'about', label: 'About' },
        { key: 'how_it_works', label: 'How it works?' }
      ]

const parseDetailTabFromUrl = (source: 'app' | 'integration', search?: string): DetailTab => {
  if (typeof window === 'undefined' && search === undefined) return 'about'
  const raw = new URLSearchParams(search ?? window.location.search).get('tab')
  const tabList = getDetailTabList(source)
  if (raw && tabList.some(tab => tab.key === raw)) return raw as DetailTab
  return 'about'
}

const AppIntegrationDetailView: React.FC<AppIntegrationDetailViewProps> = ({
  detailConfig,
  backPath = '/apps-integrations',
  additionalAppPath,
  onActivationChange
}) => {
  const { canAccess } = useUser()

  // Everything below is derived from the app detail once, under the names the rest of this view uses.
  const {
    id: appId,
    name: appKey,
    rating,
    numberOfBrandsUsing,
    externallyManaged = false,
    features,
    howItWorks,
    medias,
    byShopflo = false,
    pricingTiers,
    reviews,
    category,
    documentations,
    helpUrl,
    multiConfig = false,
    configSchema,
    merchantConfigs,
    integrationConfigSource,
    redirectUrl,
    worksWith
  } = detailConfig

  const source: 'app' | 'integration' = detailConfig.appType === 'APP' ? 'app' : 'integration'
  const description = detailConfig.description ?? detailConfig.externalDescription ?? ''
  const isActive = detailConfig.merchantAppDetails?.active ?? false

  // Only an active install has a plan — an inactive app may still carry a stale installed_tier.
  const currentPlan = isActive ? detailConfig.merchantAppDetails?.currentPlan : null
  const autoInstall = detailConfig.type === 'AUTO_INSTALL'
  const partnerSetupRequired = detailConfig.externalSupportRequired === true
  const requiresAdditionalApp =
    detailConfig.appType === 'INTEGRATION' &&
    detailConfig.category === 'SHIPPING' &&
    !canAccess(permissionType.DYNAMIC_ETD_INTEGRATION, 0)

  const router = useRouter()
  const { t } = useLocale()
  const { sendActivityLog } = useActivityLogs()
  const {
    state: { currentUpsellPlan }
  } = useUpsellPlan()

  const tabList = getDetailTabList(source)
  const [activeTab, setActiveTab] = useState<DetailTab>('about')
  const [activating, setActivating] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [updatingTier, setUpdatingTier] = useState(false)
  const [configDirty, setConfigDirty] = useState(false)
  const [activationError, setActivationError] = useState<string | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [playingMediaIndex, setPlayingMediaIndex] = useState<number | null>(null)
  const [highlightTab, setHighlightTab] = useState<DetailTab | null>(null)
  const configFormRef = useRef<ConfigSchemaFormHandle>(null)
  const aboutSectionRef = useRef<HTMLDivElement>(null)
  const howItWorksSectionRef = useRef<HTMLDivElement>(null)

  const hasMultiplePlans = pricingTiers && pricingTiers.length > 1

  // Multiple tiers → one plan per tier (tier-level features, pricing, restrictions).
  // Single tier → the app has one price: the sum of that tier's feature prices.
  const appTierFeaturePlanConfig: AppTierFeaturePlanConfig[] = useMemo(() => {
    return hasMultiplePlans ? parseAppTierFeatureConfigs(pricingTiers) : []
  }, [hasMultiplePlans, pricingTiers])

  const safeName = useMemo(
    () =>
      hasMultiplePlans && currentPlan
        ? getCurrentPlanName(appTierFeaturePlanConfig, currentPlan) ?? 'App'
        : detailConfig.displayName ?? detailConfig.name ?? 'App',
    [hasMultiplePlans, currentPlan, appTierFeaturePlanConfig, detailConfig.displayName, detailConfig.name]
  )

  const logoUrl = useMemo(
    () =>
      hasMultiplePlans && currentPlan
        ? getCurrentPlanLogoUrl(appTierFeaturePlanConfig, currentPlan)
        : medias?.find(m => m.primary)?.url ?? medias?.[0]?.url ?? undefined,
    [appTierFeaturePlanConfig, currentPlan, hasMultiplePlans, medias]
  )

  const appPrice = useMemo(
    () =>
      hasMultiplePlans
        ? sumFeaturePriceFromTiers(
            appTierFeaturePlanConfig.find(item => item.id === currentPlan)?.feature_price_mapping
          ) ?? undefined
        : sumFeaturePriceFromTiers(pricingTiers?.[0]?.feature_price_mapping) ?? undefined,
    [appTierFeaturePlanConfig, currentPlan, hasMultiplePlans, pricingTiers]
  )

  const markedUpPrice = useMemo(
    () =>
      hasMultiplePlans
        ? appTierFeaturePlanConfig.find(item => item.id === currentPlan)?.marked_up_price ?? 0
        : pricingTiers?.[0]?.marked_up_price ?? detailConfig.markedUpPrice ?? 0,
    [appTierFeaturePlanConfig, currentPlan, hasMultiplePlans, pricingTiers, detailConfig.markedUpPrice]
  )

  const isIntegration = source === 'integration'
  const hasConfig = useMemo(
    () => isMeaningfullyConfigured(merchantConfigs, configSchema),
    [merchantConfigs, configSchema]
  )
  const hasV2Schema = (configSchema?.length ?? 0) > 0
  const showConfigureCta = isIntegration && activeTab !== 'configuration' && (!isActive || !hasConfig)

  const categoryLabel = category
    ? humanizeCategoryKey(APP_STORE_CATEGORY_MAP[category] ?? category.toLowerCase())
    : null

  // tierPlanId is the pricing tier id to install (e.g. 'lite' / 'plus') — omitted for single-tier apps payload.data carries app-specific extras on a retry — e.g. Upsell's active_upsell_ids after FSE_001.
  const handleAppActivate = async (
    tierPlanId?: string,
    payload?: V2AppTierUpdatePayload
  ): Promise<AppExtensionActivateResult> => {
    setActivating(true)
    setActivationError(null)
    try {
      const resp = await activateAppV2(appId, {
        ...(tierPlanId ? { tier_id: tierPlanId } : {}),
        ...(payload?.data ? { data: payload.data } : {})
      })
      if (handleAppInstallAction({ data: resp })) return { success: true }
      await onActivationChange?.()
      if (configFormRef.current) {
        const saved = await configFormRef.current.save()
        if (!saved) {
          errorToast('Activated, but config could not be saved. Please update your configuration.')
          return { success: true }
        }
      }
      successToast(source === 'app' ? 'App activated.' : 'Integration activated.')
      return { success: true }
    } catch (e) {
      // FSE_001 isn't a failure to show — the extension opens the downgrade picker and retries from there.
      if (getErrorCode(e) === TOO_MANY_ACTIVE_UPSELLS_ERROR_CODE) return { success: false, error: e }
      const msg = getActionErrorMessage(e)
      setActivationError(msg)
      errorToast(msg)
      return { success: false, error: e }
    } finally {
      setActivating(false)
    }
  }

  // Moves an installed app to another pricing tier (upgrade / degrade). tierPlanId is the target tier payload carries the app-specific extras (current tier, e.g. which upsells stay active).
  const handleAppTierUpdate = async (
    tierPlanId: string,
    payload?: V2AppTierUpdatePayload
  ): Promise<AppExtensionActivateResult> => {
    setUpdatingTier(true)
    setActivationError(null)
    try {
      await updateAppTierV2(appId, { ...payload, target_tier_id: tierPlanId })
      await onActivationChange?.()
      // No success toast here — the extension shows the right one for the move (e.g. Upsell's +Plus nudge).
      return { success: true }
    } catch (e) {
      // FSE_001 isn't a failure to show — the extension opens the downgrade picker and retries from there.
      if (getErrorCode(e) === TOO_MANY_ACTIVE_UPSELLS_ERROR_CODE) return { success: false, error: e }
      const msg = getActionErrorMessage(e)
      errorToast(msg)
      return { success: false, error: e }
    } finally {
      setUpdatingTier(false)
    }
  }

  // Per-app behavior (dialogs, menu actions, CTA overrides) is registered in src/lib/hooks/appExtensions — this view never branches on a specific app by name.
  const activeExtension = useActiveAppExtension(
    { appKey, redirectUrl, source },
    {
      currentPlan,
      isActive,
      isLoading: activating || updatingTier,
      onActivate: handleAppActivate,
      onTierUpdate: handleAppTierUpdate,
      onProceedToUninstall: () => setConfirmOpen(true)
    }
  )

  const menuActions: AppMenuAction[] = activeExtension?.menuActions ?? []

  const ctaLabel = useMemo(() => {
    if (showConfigureCta) return 'Configure'
    if (isActive) return source === 'app' ? 'Deactivate App' : 'Deactivate'
    if (source === 'app') return activeExtension?.cta?.label ?? (appPrice === 0 ? 'Install' : 'Start free trial')
    return 'Activate'
  }, [showConfigureCta, isActive, source, appPrice, activeExtension])

  const handleTabChange = (value: string) => {
    const nextTab = value as DetailTab
    if (!tabList.some(tab => tab.key === nextTab)) return

    setActiveTab(nextTab)
    syncDetailTabToUrl(nextTab)

    if (source === 'app') {
      setHighlightTab(nextTab)

      if (nextTab === 'how_it_works') {
        howItWorksSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      } else if (nextTab === 'about') {
        aboutSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }

  const handleCta = async () => {
    if (activeExtension?.cta) {
      activeExtension.cta.onClick()
      return
    }
    if (showConfigureCta) {
      handleTabChange('configuration')
      return
    }
    if (isActive) {
      setConfirmOpen(true)
      return
    }
    if (configFormRef.current) {
      const valid = configFormRef.current.validate()
      if (!valid) return
    }
    await handleAppActivate()
  }

  const handleConfirmAppUninstall = async (formData: CancellationReasonFormData) => {
    setActivating(true)
    setActivationError(null)
    try {
      await deactivateAppV2(appId, { metadata: formData })
      // Upsell's extension build() can't call hooks, so its uninstall log is sent from here.
      if (findAppExtension({ appKey, redirectUrl, source })?.key === 'upsell') {
        sendActivityLog({
          entityName: EntityName.UPSELL,
          actionName: UpsellActivityAction.UNINSTALL_CONFIRMED,
          metadata: { current_plan: currentUpsellPlan }
        })
      }
      successToast('App uninstalled.')
      await onActivationChange?.()
    } catch (e) {
      errorToast(getActionErrorMessage(e))
    } finally {
      setActivating(false)
      setConfirmOpen(false)
    }
  }

  const handleConfirmIntegrationDeactivate = async () => {
    setActivating(true)
    setActivationError(null)
    try {
      await deactivateAppV2(appId)
      successToast('Integration deactivated.')
      await onActivationChange?.()
    } catch (e) {
      errorToast(getActionErrorMessage(e))
    } finally {
      setActivating(false)
      setConfirmOpen(false)
    }
  }

  const handleCloseDeactivateDialog = () => {
    if (activating) return
    setConfirmOpen(false)
  }

  const handleUpdate = async () => {
    setUpdating(true)
    try {
      const ok = await configFormRef.current?.save()
      if (ok) onActivationChange?.()
    } catch (e) {
      errorToast(getActionErrorMessage(e))
    } finally {
      setUpdating(false)
    }
  }

  const handleBack = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      router.back()
      return
    }
    const fallbackTab: TopLevelTab = source === 'integration' ? 'integrations' : 'apps'
    router.push(appsIntegrationsListPath(fallbackTab, backPath))
  }

  const handleViewApp = () => {
    if (redirectUrl) router.push(redirectUrl)
  }

  const handleManage = () => router.push('/apps-integrations/manage')

  const handleAdditionalAppClick = () => router.push(additionalAppPath || '/apps-integrations')

  useLayoutEffect(() => {
    // Static export pre-renders without window; apply ?tab= once after hydration.
    setActiveTab(parseDetailTabFromUrl(source))
  }, [source])

  const mediaItems = medias?.filter(m => m.url && !m.primary) ?? []
  const howItWorksLines = splitHowItWorks(howItWorks)

  return (
    <div className='flex min-h-0 w-full flex-1 flex-col gap-0 bg-zinc-50'>
      <div className='flex shrink-0 flex-col bg-zinc-50 px-12 pt-4 pb-2'>
        <div className='flex items-center gap-2 py-2'>
          <IconButton
            variant='outline'
            size='sm'
            IconComponent={CaretLeft}
            iconProps={{ weight: 'bold' }}
            className='text-zinc-500'
            onClick={handleBack}
          />
          <AppLogo src={logoUrl} name={safeName} size='sm' bare className='shadow-[inset_0_0_1px_0_rgba(0,0,0,0.05)]' />
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList variant='neutral'>
              {tabList.map(tab => (
                <TabsTrigger key={tab.key} value={tab.key}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </div>

      <div className='relative z-0 min-h-0 flex-1'>
        <div className='h-full min-h-0 overflow-y-auto'>
          <div className='flex min-h-full flex-row items-stretch'>
            {/* Left — main content */}
            <div className='min-w-0 flex-1 flex justify-center px-12 pb-6 min-xl:mt-4'>
              <div className='w-full max-w-[90ch]'>
                {(source === 'app' || activeTab === 'about') && (
                  <div ref={aboutSectionRef} className='relative scroll-mt-4 rounded-xl'>
                    {mediaItems.length > 0 && (
                      <DetailMediaCarousel
                        items={mediaItems}
                        fallbackLabel={safeName}
                        playingMediaIndex={playingMediaIndex}
                        onPlayMedia={setPlayingMediaIndex}
                      />
                    )}
                    <div className='py-6 px-1.5 flex w-full flex-col gap-8 max-w-[90ch]'>
                      {description && <div dangerouslySetInnerHTML={{ __html: description }} />}
                      {features && <div dangerouslySetInnerHTML={{ __html: features }} />}
                    </div>
                    {source === 'app' && (
                      <div
                        aria-hidden
                        className={classNames(
                          HIGHLIGHT_OVERLAY_CLASS,
                          highlightTab === 'about' ? 'animate-section-highlight' : ''
                        )}
                        onAnimationEnd={e => {
                          if (e.target === e.currentTarget) setHighlightTab(null)
                        }}
                      />
                    )}
                  </div>
                )}

                {source === 'app' && (
                  <div ref={howItWorksSectionRef} className='relative space-y-4 py-6 px-1.5 scroll-mt-4 rounded-xl'>
                    <h3 className='text-text-md-semibold text-zinc-800'>How it works?</h3>
                    {howItWorksLines.length > 1 ? (
                      <ul className='mt-6 flex flex-col gap-2'>
                        {howItWorksLines.map((line, i) => (
                          <li key={i} className='flex items-start gap-2'>
                            <span className='mt-2 w-1.5 h-1.5 rounded-full bg-zinc-400 flex-shrink-0' />
                            <span className='text-text-sm-regular text-zinc-500'>
                              {line.replace(/^\s*[-•]\s*/, '')}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : howItWorksLines.length === 1 ? (
                      // Single point → plain text, no bullet.
                      <p className='text-text-sm-regular text-zinc-500'>
                        {howItWorksLines[0].replace(/^\s*[-•]\s*/, '')}
                      </p>
                    ) : (
                      <p className='text-text-sm-regular text-zinc-500'>No details available.</p>
                    )}
                    <div
                      aria-hidden
                      className={classNames(
                        HIGHLIGHT_OVERLAY_CLASS,
                        highlightTab === 'how_it_works' ? 'animate-section-highlight' : ''
                      )}
                      onAnimationEnd={e => {
                        if (e.target === e.currentTarget) setHighlightTab(null)
                      }}
                    />
                  </div>
                )}

                {activeTab === 'configuration' && (
                  <div className='relative flex w-full justify-center rounded-xl'>
                    <div className='flex w-full max-w-[640px] flex-col gap-2 py-3'>
                      {partnerSetupRequired && (
                        <div className='flex items-center justify-between gap-4 pl-4 pr-1.5 py-2.5 rounded-lg border border-amber-200 bg-yellow-50'>
                          <div className='flex items-start gap-2 w-[80%]'>
                            <span className='mt-0.5 text-amber-500 flex-shrink-0'>
                              <Icon IconComponent={Info} size='sm' iconProps={{ weight: 'bold' }} />
                            </span>
                            <div className='w-full'>
                              <p className='text-text-xs-semibold text-subtext-darker'>Partner setup is pending!</p>
                              <p className='text-text-xs-regular text-zinc-500'>
                                This app is third-party and requires full setup by your partner. Without it, the app
                                won&apos;t function correctly.
                              </p>
                            </div>
                          </div>
                          <div className='px-1.5'>
                            {documentations?.[0]?.url && (
                              <a
                                href={documentations[0].url}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='flex-shrink-0 flex items-center gap-1 text-text-xs-medium text-zinc-600 hover:text-zinc-900 transition-colors'
                              >
                                Setup Now
                                <Icon IconComponent={CaretRight} size='xxs' iconProps={{ weight: 'bold' }} />
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                      {hasV2Schema ? (
                        <ConfigSchemaForm
                          ref={configFormRef}
                          appId={appId}
                          configSchema={configSchema!}
                          existingConfigs={merchantConfigs}
                          integrationConfigSource={integrationConfigSource}
                          multiConfig={multiConfig}
                          onDirtyChange={setConfigDirty}
                        />
                      ) : (
                        <p className='text-text-sm-regular text-zinc-500'>
                          No configuration required for this {source}.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right sidebar */}
            <div className='flex w-[390px] flex-shrink-0'>
              <aside className='flex min-w-0 flex-1 flex-col py-6 sticky top-0 h-fit self-start px-10'>
                {isIntegration ? (
                  <IntegrationDetailSidebar
                    name={safeName}
                    logoUrl={logoUrl}
                    categoryLabel={categoryLabel}
                    isActive={isActive}
                    externallyManaged={externallyManaged}
                    activating={activating}
                    activationError={activationError}
                    ctaLabel={ctaLabel}
                    isConfigureCta={showConfigureCta}
                    onCtaClick={handleCta}
                    showUpdateCta={configDirty && isActive && activeTab === 'configuration'}
                    updating={updating}
                    onUpdateClick={handleUpdate}
                    documentations={documentations}
                    helpUrl={helpUrl}
                    requiresAdditionalApp={requiresAdditionalApp}
                    onAdditionalAppClick={handleAdditionalAppClick}
                  />
                ) : (
                  <AppDetailSidebar
                    name={safeName}
                    logoUrl={logoUrl}
                    byShopflo={byShopflo}
                    autoInstall={autoInstall}
                    categoryLabel={categoryLabel}
                    price={appPrice}
                    markedUpPrice={markedUpPrice}
                    numberOfBrandsUsing={numberOfBrandsUsing}
                    rating={rating}
                    reviewCount={reviews?.length}
                    isActive={isActive}
                    activating={activating}
                    menuActions={menuActions}
                    activationError={activationError}
                    ctaLabel={ctaLabel}
                    onCtaClick={handleCta}
                    redirectUrl={redirectUrl}
                    onViewApp={handleViewApp}
                    onManage={handleManage}
                    worksWith={worksWith}
                    documentations={documentations}
                    helpUrl={helpUrl}
                    sidebarSlot={activeExtension?.sidebarSlot}
                  />
                )}
              </aside>
            </div>
          </div>
        </div>
        <div
          aria-hidden
          className='pointer-events-none absolute inset-y-0 right-[390px] w-px [background:linear-gradient(180deg,theme(colors.zinc.200/0)_0%,theme(colors.zinc.200)_9.61%)]'
        />
      </div>

      {isIntegration ? (
        <DeleteConfirmationPopup
          isOpen={confirmOpen}
          actions={{ onConfirm: handleConfirmIntegrationDeactivate, onClose: handleCloseDeactivateDialog }}
          config={{
            title: 'Deactivate integration?',
            description: `${safeName} will stop syncing until you reactivate it.`,
            confirmationText: 'Yes, deactivate',
            isLoading: activating
          }}
        />
      ) : (
        <>
          {/* The dialogs for the active extensions will be automatically rendered*/}
          {activeExtension?.renderDialogs()}
          <CancellationReasonDialog
            isOpen={confirmOpen}
            subtitle={safeName}
            title={t('cancel_subscription')}
            isLoading={activating}
            onClose={handleCloseDeactivateDialog}
            onConfirm={handleConfirmAppUninstall}
          />
        </>
      )}
    </div>
  )
}

export default AppIntegrationDetailView
